// Notification Sound Service for J-Style Services
// Handles playing notification sounds for ride alerts

// Preset notification sounds (using Web Audio API tones)
// Updated: LOUDER and LONGER sounds for better alerts
export const NOTIFICATION_SOUNDS = {
  default: { name: 'Default Chime', frequency: 587.33, duration: 1.2, volume: 1.0 },
  urgent: { name: 'Urgent Alert', frequency: 880, duration: 0.8, volume: 1.0, repeat: 3 },
  soft: { name: 'Soft Bell', frequency: 440, duration: 1.0, volume: 0.8 },
  double: { name: 'Double Beep', frequency: 659.25, duration: 0.6, repeat: 3, volume: 1.0 },
  triple: { name: 'Triple Chime', frequency: 523.25, duration: 0.5, repeat: 4, volume: 1.0 },
  melody: { name: 'Melody', frequency: 0, duration: 0, custom: 'melody', volume: 1.0 },
  loud: { name: 'Loud Alert', frequency: 740, duration: 1.5, volume: 1.0, repeat: 2 },
};

// Storage keys
const SOUND_SETTINGS_KEY = 'jstyle_notification_sound';
const SOUND_ENABLED_KEY = 'jstyle_sound_enabled';
const VIBRATION_ENABLED_KEY = 'jstyle_vibration_enabled';

// Audio context for generating tones
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Play a simple tone - LOUDER and LONGER
const playTone = (frequency, duration, volume = 1.0) => {
  const ctx = getAudioContext();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';
  
  // Start at full volume
  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  // Hold the volume for most of the duration, then fade
  gainNode.gain.setValueAtTime(volume, ctx.currentTime + duration * 0.7);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
  
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
};

// Play melody (more pleasant notification) - LONGER and LOUDER
const playMelody = () => {
  const ctx = getAudioContext();
  // Extended melody: C5, E5, G5, C6 played twice
  const notes = [523.25, 659.25, 783.99, 1046.50, 783.99, 659.25, 523.25];
  
  notes.forEach((freq, index) => {
    setTimeout(() => {
      playTone(freq, 0.5, 1.0);  // Longer duration (0.5s) and full volume
    }, index * 250);  // More spacing between notes
  });
};

// Play notification sound - LOUDER and LONGER
export const playNotificationSound = (soundKey = null) => {
  const enabled = getSoundEnabled();
  if (!enabled) return;
  
  const selectedSound = soundKey || getSelectedSound();
  const sound = NOTIFICATION_SOUNDS[selectedSound];
  
  if (!sound) return;
  
  // Resume audio context if suspended (browser autoplay policy)
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  
  const volume = sound.volume || 1.0;
  
  if (sound.custom === 'melody') {
    playMelody();
  } else if (sound.repeat) {
    for (let i = 0; i < sound.repeat; i++) {
      setTimeout(() => {
        playTone(sound.frequency, sound.duration, volume);
      }, i * (sound.duration * 1000 + 200));  // More gap between repeats
    }
  } else {
    playTone(sound.frequency, sound.duration, volume);
  }
  
  // Vibrate longer if enabled and supported
  if (getVibrationEnabled() && navigator.vibrate) {
    navigator.vibrate([300, 150, 300, 150, 300]);  // Longer vibration pattern
  }
};

// Get selected sound from storage
export const getSelectedSound = () => {
  return localStorage.getItem(SOUND_SETTINGS_KEY) || 'default';
};

// Set selected sound
export const setSelectedSound = (soundKey) => {
  localStorage.setItem(SOUND_SETTINGS_KEY, soundKey);
};

// Get sound enabled status
export const getSoundEnabled = () => {
  const stored = localStorage.getItem(SOUND_ENABLED_KEY);
  return stored === null ? true : stored === 'true';
};

// Set sound enabled status
export const setSoundEnabled = (enabled) => {
  localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
};

// Get vibration enabled status
export const getVibrationEnabled = () => {
  const stored = localStorage.getItem(VIBRATION_ENABLED_KEY);
  return stored === null ? true : stored === 'true';
};

// Set vibration enabled status
export const setVibrationEnabled = (enabled) => {
  localStorage.setItem(VIBRATION_ENABLED_KEY, String(enabled));
};

// Test notification sound - LOUDER and LONGER
export const testNotificationSound = (soundKey) => {
  const sound = NOTIFICATION_SOUNDS[soundKey];
  if (!sound) return;
  
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  
  const volume = sound.volume || 1.0;
  
  if (sound.custom === 'melody') {
    playMelody();
  } else if (sound.repeat) {
    for (let i = 0; i < sound.repeat; i++) {
      setTimeout(() => {
        playTone(sound.frequency, sound.duration, volume);
      }, i * (sound.duration * 1000 + 200));
    }
  } else {
    playTone(sound.frequency, sound.duration, volume);
  }
  
  if (navigator.vibrate) {
    navigator.vibrate([300, 150, 300, 150, 300]);
  }
};

export default {
  playNotificationSound,
  testNotificationSound,
  getSelectedSound,
  setSelectedSound,
  getSoundEnabled,
  setSoundEnabled,
  getVibrationEnabled,
  setVibrationEnabled,
  NOTIFICATION_SOUNDS,
};
